import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import Table from "../../../core/common/dataTable/index";
import Toasts from "../../../core/common/toast";
import { all_routes } from "../../router/all_routes";
import dayjs from "dayjs";
import { workSchedule } from "../../../core/data/redux/rolePermissionsSlice";

const WorkSchedule = () => {
  const dispatch = useDispatch();
  const roleState = useSelector((state: any) => state.roleState);
  const routes = all_routes;

  const [dataList, setDataList] = useState<any[]>([]);
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const fetchRecords = () => {
    dispatch(
      workSchedule({
        page: pagination.current,
        limit: pagination.pageSize,
        query: debouncedQuery,
      })
    );
  };

  // Debounce search query
  useEffect(() => {
    const delay = setTimeout(() => {
      setPagination((prev) => ({ ...prev, current: 1 }));
      setDebouncedQuery(query);
    }, 500);
    return () => clearTimeout(delay);
  }, [query]);

  // Fetch on initial mount or search change
  useEffect(() => {
    fetchRecords();
  }, [pagination.current, pagination.pageSize, debouncedQuery]);

  useEffect(() => {
    if (
      roleState.loading === "fulfilled" &&
      roleState.action === "assign-permission"
    ) {
    } else if (
      roleState.loading === "fulfilled" &&
      roleState.action === "update"
    ) {
      setTimeout(fetchRecords, 500);
    } else if (
      roleState.loading === "fulfilled" &&
      roleState.action === "list"
    ) {
      const result = roleState?.data?.data;
      if (result?.data?.length > 0) {
        setDataList(result.data);
        setPagination((prev) => ({
          ...prev,
          total: result.total || result.data.length,
          current: result.page || 1,
          pageSize: result.limit || 10,
        }));
      } else {
        setDataList([]);
      }
    }
  }, [roleState.loading]);

  const filteredData = query
    ? dataList?.filter((item) =>
        item?.wSrule?.toLowerCase().includes(query.toLowerCase())
      )
    : dataList;

  const handleSearch = (value: string) => {
    setQuery(value);
  };
  const handlePagination = (paginationInfo: any) => {
    setPagination((prev) => ({
      ...prev,
      current: paginationInfo.current,
      pageSize: paginationInfo.pageSize,
    }));
  };
  useEffect(() => {
    const delay = setTimeout(() => {
      setPagination((prev) => ({ ...prev, current: 1 }));
      setDebouncedQuery(query);
    }, 500);
    return () => clearTimeout(delay);
  }, [query]);

  const columns = [
    {
      title: "Sr. No.",
      dataIndex: "id",
      width: 80,
      render: (text: string, record: any, index: number) => (
        <div>
          {pagination.current
            ? index + (pagination.current - 1) * pagination.pageSize + 1
            : index + 1}
        </div>
      ),
    },
    {
      title: "Work Schedule Rule",
      dataIndex: "wSrule",
    },
    {
      title: "Year",
      dataIndex: "calendarYear",
    },
    {
      title: "Month",
      dataIndex: "month",
      render: (text: number) => dayjs(`${text}`, "M").format("MMMM"), // Convert 6 → June
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      render: (text: string) => dayjs(text).format("DD-MM-YYYY hh:mm A"),
    },
    {
      title: "Updated At",
      dataIndex: "updatedAt",
      render: (text: string) => dayjs(text).format("DD-MM-YYYY hh:mm A"),
    },
    {
      title: "Schedule Rules",
      dataIndex: "schedule_rules",
      render: (text: string) => {
        if (!text) {
          return <span className="text-muted">No rules</span>;
        }
        
        try {
          let parsed;
          
          // Try different parsing strategies
          if (typeof text === 'string') {
            // First, try parsing as-is
            try {
              parsed = JSON.parse(text);
            } catch (e) {
              // If that fails, try cleaning the string
              let cleanedText = text
                .replace(/'/g, '"')  // Replace single quotes with double quotes
                .replace(/\\"/g, '"')  // Remove escaped double quotes
                .replace(/\\\\/g, '\\')  // Fix double backslashes
                .trim();
              
              // Remove outer quotes if present
              if (cleanedText.startsWith('"') && cleanedText.endsWith('"')) {
                cleanedText = cleanedText.slice(1, -1);
              }
              
              parsed = JSON.parse(cleanedText);
            }
          } else {
            // If it's already an object
            parsed = text;
          }

          if (!parsed || typeof parsed !== 'object') {
            return <span className="text-muted">Invalid data format</span>;
          }

          return (
            <div
              className="schedule-scroll"
              style={{ maxHeight: "100px", overflowY: "auto" }}
            >
              {Object.entries(parsed).map(([day, ruleObj]: any) => {
                if (!ruleObj || typeof ruleObj !== 'object') {
                  return (
                    <div key={day} style={{ padding: "2px", fontSize: "13px" }}>
                      <span>Day {day}: Invalid rule format</span>
                    </div>
                  );
                }
                
                const ruleCode = Object.keys(ruleObj)[0];
                const ruleValue = ruleObj[ruleCode];
                const ruleDesc = typeof ruleValue === 'string' 
                  ? ruleValue.replace(/"/g, "")
                  : String(ruleValue);
                
                return (
                  <div
                    key={day}
                    style={{
                      display: "flex",
                      gap: "55px",
                      padding: "2px",
                      fontSize: "13px",
                      cursor: "pointer",
                    }}
                  >
                    <div style={{ fontWeight: 400 }}>Day {day}</div>
                    <div
                      style={{
                        color:
                          ruleDesc.toUpperCase().includes("OFF") ? "#dc3545" : "#28a745",
                        fontWeight: 500,
                      }}
                    >
                      {ruleDesc}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        } catch (error) {
          console.error('Schedule Rules parsing error:', error, 'Data:', text);
          return (
            <div>
              <span className="text-danger">Parse Error</span>
              <br />
              <small className="text-muted" style={{ fontSize: "10px" }}>
                {String(text).substring(0, 50)}...
              </small>
            </div>
          );
        }
      },
    },
  ];

  return (
    <div>
      <Toasts
        show={roleState.loading === "rejected"}
        message={roleState.error}
        title={"Failed"}
        type={"danger"}
      />
      <Toasts
        show={
          roleState.loading === "fulfilled" &&
          roleState.action !== "list" &&
          roleState.action !== "get-permission"
        }
        message={roleState?.data?.message}
        title={"Success"}
        type={"success"}
      />

      <div className="page-wrapper">
        <div className="content">
          <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
            <div className="my-auto mb-2">
              <h3 className="page-title mb-1">Work schedule</h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={routes.adminDashboard}>Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to="#">Administration</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Work schedule
                  </li>
                </ol>
              </nav>
            </div>
          </div>

          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
              <h4 className="mb-3">Work schedule List</h4>
            </div>
            <div className="card-body p-0 py-3">
              <Table
                columns={columns}
                dataSource={filteredData}
                Selection={false}
                paginationConfig={pagination}
                handlePagination={handlePagination}
                handleSearch={handleSearch}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkSchedule;
